import type { Locale } from '@/lib/i18n/config'
import { getLocalizedNodeUrl, getLocalizedUrl } from '@/lib/site/config'
import {
  type IntegrationsContentTranslator,
  integrationDescription,
} from '@/lib/site/seo/integration-content'
import type { SeoTranslator } from '@/lib/site/seo/seo-translator'
import { integrationData } from './data'
import { getIntegrationTypeLabel, type IntegrationMeta } from './types'

/** Crawlable canonical; `/integrations` is the signed-in alias crawlers get redirected from. */
const CATALOG_PATH = '/browse/integrations'

/**
 * The page's locale, its `seo` messages and the integrations' own words
 * (`integrationsContent`): the public `/browse/integrations` pages pass
 * their route locale, the in-app `/integrations` pages the reader's.
 */
type IntegrationJsonLdLocale = {
  locale: Locale
  t: SeoTranslator
  content: IntegrationsContentTranslator
}

type Labels = {
  home: string
  integrations: string
  listName: string
  listDescription: string
  itemName: (name: string) => string
  authentication: string
  dataDirection: string
  dataIn: string
  dataOut: string
  availability: string
  available: string
  comingSoon: string
}

/** The labels in the page's language. */
function integrationJsonLdLabels({ t }: IntegrationJsonLdLocale): Labels {
  return {
    home: t('jsonLd.breadcrumb.home'),
    integrations: t('jsonLd.breadcrumb.integrations'),
    listName: t('jsonLd.integrations.listName'),
    listDescription: t('jsonLd.integrations.listDescription'),
    itemName: (name) => t('jsonLd.integrations.itemName', { name }),
    authentication: t('jsonLd.integrations.authentication'),
    dataDirection: t('jsonLd.integrations.dataDirection'),
    dataIn: t('jsonLd.integrations.dataIn'),
    dataOut: t('jsonLd.integrations.dataOut'),
    availability: t('jsonLd.integrations.availability'),
    available: t('jsonLd.integrations.available'),
    comingSoon: t('jsonLd.integrations.comingSoon'),
  }
}

/** `path` on this site, in the page's locale. */
function urlOf(localized: IntegrationJsonLdLocale, path: string): string {
  return getLocalizedUrl(localized.locale, path)
}

/** The home page, as structured data names it. */
function homeUrlOf(localized: IntegrationJsonLdLocale): string {
  return getLocalizedNodeUrl(localized.locale, '/')
}

function integrationProperties(integration: IntegrationMeta, labels: Labels) {
  return [
    {
      '@type': 'PropertyValue',
      name: labels.authentication,
      // The connection type stays English: a technical term ("OAuth",
      // "API key") the structured data names the same in every language.
      value: getIntegrationTypeLabel(integration.integrationType),
    },
    {
      '@type': 'PropertyValue',
      name: labels.dataDirection,
      value:
        integration.dataDirection === 'data_in'
          ? labels.dataIn
          : labels.dataOut,
    },
    {
      '@type': 'PropertyValue',
      name: labels.availability,
      value:
        integration.availability === 'coming_soon'
          ? labels.comingSoon
          : labels.available,
    },
  ]
}

/**
 * `ItemList` JSON-LD describing the integrations catalog.
 *
 * Why ItemList:
 *  - Search engines treat ItemList as a legitimate "list of things"
 *    (Google supports it for carousels and rich results).
 *  - Generative answer engines (Perplexity, ChatGPT browse, Gemini)
 *    consume JSON-LD heavily for "list of integrations" style queries
 *    ("what does Brew integrate with?"), so this is the highest-leverage
 *    SEO + GEO signal we can add for this page.
 *
 * Each item is a `SoftwareApplication` so engines can resolve which Brew
 * provider wraps the third-party tool, with a deep link back to the
 * per-provider URL on this site.
 */
export function getIntegrationsItemListJsonLd(
  localized: IntegrationJsonLdLocale
) {
  const labels = integrationJsonLdLabels(localized)
  const items = integrationData.map((integration, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'SoftwareApplication',
      name: labels.itemName(integration.name),
      // Coming-soon providers have no public page (it 404s), so no URL.
      ...(integration.availability === 'available'
        ? { url: urlOf(localized, `${CATALOG_PATH}/${integration.id}`) }
        : {}),
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: integrationDescription(localized.content, integration.id),
      isAccessibleForFree: true,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      additionalProperty: integrationProperties(integration, labels),
    },
  }))

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${urlOf(localized, CATALOG_PATH)}#catalog`,
    name: labels.listName,
    description: labels.listDescription,
    url: urlOf(localized, CATALOG_PATH),
    numberOfItems: integrationData.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: items,
  }
}

/**
 * Cross-page breadcrumb for `/browse/integrations` so search engines render the
 * full path on the SERP. Cheap to add; large UX win on Google.
 */
export function getIntegrationsBreadcrumbJsonLd(
  localized: IntegrationJsonLdLocale
) {
  const labels = integrationJsonLdLabels(localized)
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: labels.home,
        item: homeUrlOf(localized),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: labels.integrations,
        item: urlOf(localized, CATALOG_PATH),
      },
    ],
  }
}

/**
 * Per-integration `SoftwareApplication` JSON-LD for the dedicated
 * `/browse/integrations/<provider>` page. Engines can resolve the page as a
 * concrete software entry rather than a generic catalog item, which
 * helps generative answer engines cite the right URL when asked
 * questions like "does Brew support Stripe webhooks?".
 */
export function getIntegrationSoftwareJsonLd(
  integration: IntegrationMeta,
  localized: IntegrationJsonLdLocale
) {
  const labels = integrationJsonLdLabels(localized)
  const url = urlOf(localized, `${CATALOG_PATH}/${integration.id}`)
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': url,
    name: labels.itemName(integration.name),
    url,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: integrationDescription(localized.content, integration.id),
    isAccessibleForFree: true,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    additionalProperty: integrationProperties(integration, labels),
  }
}

/**
 * Per-integration breadcrumb extending the catalog breadcrumb with the
 * specific provider as the third crumb.
 */
export function getIntegrationBreadcrumbJsonLd(
  integration: IntegrationMeta,
  localized: IntegrationJsonLdLocale
) {
  const labels = integrationJsonLdLabels(localized)
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: labels.home,
        item: homeUrlOf(localized),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: labels.integrations,
        item: urlOf(localized, CATALOG_PATH),
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: integration.name,
        item: urlOf(localized, `${CATALOG_PATH}/${integration.id}`),
      },
    ],
  }
}
